// 게시글 본문(content) HTML에서 "자주 묻는 질문" FAQ 블록을 추출/분리한다.
//
// SEO/AEO/GEO 목적: FAQ를 본문(Shadow DOM, CSR)에서 떼어내
// 서버에서 light DOM 시맨틱 HTML + FAQPage(JSON-LD)로 렌더하기 위한 파서.
//
// 대상 마크업(관리자 에디터가 생성하는 표준 형태):
//   <h2>N. ○○ 자주 묻는 질문</h2>
//   <dl>
//     <dt ...>Q. 질문</dt>
//     <dd ...>A. 답변</dd>
//     ... (반복)
//   </dl>
//
// 서버 컴포넌트와 클라이언트 컴포넌트에서 동일하게 쓰이므로
// DOM(document) 의존 없이 문자열 처리만으로 구현한다.

export interface FaqItem {
  question: string;
  answer: string;
}

export interface ParsedFaq {
  faqs: FaqItem[];
  /** FAQ 블록(제목 + dl)을 제거한 나머지 본문 HTML */
  contentWithoutFaq: string;
}

// "자주 묻는 질문" 제목 <h2> 바로 뒤에 오는 <dl>...</dl>까지 통째로 매칭.
// 최상단 글 제목 h2("…코스 분석과 자주 묻는 질문")는 뒤에 <dl>이 없어 매칭되지 않는다.
const FAQ_BLOCK_RE =
  /<h2[^>]*>[^<]*?자주\s*묻는\s*질문[^<]*?<\/h2>\s*<dl[\s\S]*?<\/dl>/i;

function decodeEntities(input: string): string {
  return input
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&apos;/g, "'");
}

// 전역 정규식의 첫 번째 캡처 그룹을 모두 수집한다.
// (tsconfig target 하위 호환을 위해 matchAll 스프레드 대신 exec 루프 사용)
function matchAllGroups(input: string, re: RegExp): string[] {
  const out: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(input)) !== null) out.push(m[1]);
  return out;
}

function stripTags(input: string): string {
  return decodeEntities(input.replace(/<[^>]+>/g, ''))
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * 본문 HTML에서 FAQ를 추출하고, FAQ 블록을 제거한 본문을 함께 반환한다.
 * FAQ 블록이 없으면 faqs는 빈 배열, contentWithoutFaq는 원본 그대로.
 */
export function parseFaqFromContent(
  html: string | null | undefined,
): ParsedFaq {
  if (!html) return { faqs: [], contentWithoutFaq: html ?? '' };

  const blockMatch = html.match(FAQ_BLOCK_RE);
  if (!blockMatch) return { faqs: [], contentWithoutFaq: html };

  const dl = blockMatch[0].match(/<dl[\s\S]*?<\/dl>/i)?.[0] ?? '';
  const dts = matchAllGroups(dl, /<dt[^>]*>([\s\S]*?)<\/dt>/gi);
  const dds = matchAllGroups(dl, /<dd[^>]*>([\s\S]*?)<\/dd>/gi);

  const faqs: FaqItem[] = [];
  const pairCount = Math.min(dts.length, dds.length);
  for (let i = 0; i < pairCount; i++) {
    const question = stripTags(dts[i])
      .replace(/^Q[.\s]*/i, '')
      .trim();
    const answer = stripTags(dds[i])
      .replace(/^A[.\s]*/i, '')
      .trim();
    if (question && answer) faqs.push({ question, answer });
  }

  const contentWithoutFaq = html.replace(FAQ_BLOCK_RE, '').trim();
  return { faqs, contentWithoutFaq };
}

export interface SplitContent {
  /** 본문 앞부분(마무리 섹션까지) */
  main: string;
  /** 말미 프로모션 블록(예약 배너 + "함께 보면 좋은 글"). 없으면 빈 문자열 */
  trailing: string;
}

const maxOf = (a: number[]): number => a.reduce((m, x) => (x > m ? x : m), -1);
const minOf = (a: number[]): number =>
  a.reduce((m, x) => (x < m ? x : m), Number.MAX_SAFE_INTEGER);

/**
 * 본문(FAQ 제거본)에서 말미 프로모션 블록을 분리한다.
 * 일부 구(舊)포맷 글은 마무리 섹션 뒤에 예약 배너 이미지와 "함께 보면 좋은 글"
 * 링크가 붙어 있어, FAQ를 마무리 바로 다음(프로모션 앞)에 배치하기 위해 분할한다.
 * 프로모션 블록이 없으면 trailing은 빈 문자열이고 main이 전체가 된다.
 */
export function splitTrailingPromo(
  html: string | null | undefined,
): SplitContent {
  if (!html) return { main: html ?? '', trailing: '' };

  const candidates: number[] = [];

  // 예약 배너 이미지(onclick=openReserveModal 또는 …-sum_ 파일명)를 감싸는 블록 시작
  const banner = html.match(
    /<img[^>]*(?:openReserveModal|[0-9]{4,}-sum_)[^>]*>/i,
  );
  if (banner) {
    const imgIdx = html.indexOf(banner[0]);
    const starts = ['<div', '<figure', '<hr', '<p'].map((t) =>
      html.lastIndexOf(t, imgIdx),
    );
    const s = maxOf(starts);
    candidates.push(s >= 0 ? s : imgIdx);
  }

  // "함께 보면 좋은 글" 관련 링크 블록 시작
  const ti = html.indexOf('함께 보면 좋은 글');
  if (ti >= 0) {
    const starts = ['<div', '<hr', '<p', '<span', '<b'].map((t) =>
      html.lastIndexOf(t, ti),
    );
    const s = maxOf(starts);
    candidates.push(s >= 0 ? s : ti);
  }

  if (!candidates.length) return { main: html, trailing: '' };
  const idx = minOf(candidates);

  let main = html.slice(0, idx);
  const trailing = html.slice(idx);

  // 마무리 섹션 끝의 빈 여백(빈 문단·<br>·<hr> 등)을 제거해 FAQ와의 간격을 줄인다.
  // FAQ와 프로모션(배너 등) 사이 간격은 컴포넌트에서 마진으로 준다.
  const ONE_EMPTY =
    /(?:\s|&nbsp;|\u200B|<br\b[^>]*\/?>|<hr\b[^>]*\/?>|<(p|div|span)\b[^>]*>(?:\s|&nbsp;|\u200B|<br\b[^>]*\/?>|<\/?(?:span|b|strong|mark|u|i|em|font)\b[^>]*>)*<\/\1>)$/i;
  let m: RegExpMatchArray | null;
  while ((m = main.match(ONE_EMPTY)) && m[0].length) {
    main = main.slice(0, main.length - m[0].length);
  }

  return { main, trailing };
}
